import { z } from "zod";
import {
	sanitizedEmailSchema,
	sanitizedNameSchema,
	sanitizedOptionalString,
} from "../validation";

const isoCountryCodeSchema = sanitizedOptionalString({ min: 2, max: 2 }).refine(
	(value: string | undefined) => !value || /^[A-Z]{2}$/.test(value),
	{ message: "Country must use ISO 3166-1 alpha-2" },
);

const optionalVatSchema = sanitizedOptionalString({ max: 32 });

export const legalLocaleSchema = z.enum(["en", "fi"]);

export const organizationAgreementAcceptanceSchema = z
	.object({
		termsOfService: z.literal(true),
		dataProcessingAgreement: z.literal(true),
		locale: legalLocaleSchema.optional(),
	})
	.strict();

export const billingInfoSchema = z
	.object({
		email: sanitizedEmailSchema,
		firstName: sanitizedOptionalString({ max: 120 }),
		lastName: sanitizedOptionalString({ max: 120 }),
		company: sanitizedOptionalString({ max: 160 }),
		phone: sanitizedOptionalString({ max: 32 }),
		country: isoCountryCodeSchema,
		vatNumber: optionalVatSchema,
	})
	.strict();

export const createOrganizationInputSchema = z
	.object({
		name: sanitizedNameSchema({ min: 2, max: 120 }),
		billingInfo: billingInfoSchema.optional(),
		acceptedAgreements: organizationAgreementAcceptanceSchema,
	})
	.strict();

export type CreateOrganizationInput = z.infer<
	typeof createOrganizationInputSchema
>;

export const inviteConsentSchema = z
	.object({
		consentTypeId: z.string().uuid(),
		consented: z.boolean(),
	})
	.strict();

export const acceptInviteAsNewUserInputSchema = z
	.object({
		token: z.string().min(1),
		name: sanitizedNameSchema({ min: 1, max: 120 }),
		locale: legalLocaleSchema.optional(),
		consents: z.array(inviteConsentSchema).min(1),
	})
	.strict();

export type AcceptInviteAsNewUserInput = z.infer<
	typeof acceptInviteAsNewUserInputSchema
>;

export const updateOrganizationInputSchema = z
	.object({
		id: z.string().uuid(),
		name: sanitizedNameSchema({ min: 2, max: 120 }),
	})
	.strict();

export type UpdateOrganizationInput = z.infer<
	typeof updateOrganizationInputSchema
>;
